import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
	Button,
	Confirm,
	Icons,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
} from '@components/common';
import PageTitle from '@components/common/PageTitle/PageTitle';
import ProfileTabSection from '@components/common/ProfileTabSection';
import TextAvatar from '@components/common/TextAvatar';
import OrganisationForm from '@components/ModalComponents/OrganisationForm';

import { auth, common } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { formatDate } from '@utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IOrganisationProfile } from '@interfaces/organisation/organisations';

import { EAdminPanelPageTitles, EOrganizationType, TOrganizationType } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { AOAccessRoleAvailability, backPagePath, getTabs, tabsNames } from './constants';

import AOAccess from './AOAccess';
import AssetOwnerSettings from './AssetOwnerSettings';
import AssetsDashboard from './AssetsDashboard';
import ManageKaiAssistant from './ManageKaiAssistant';
import OrganisationAdminProjectsContainer from './OrganisationAdminProjectsContainer';
import PlatformOwnerProjectsContainer from './PlatformOwnerProjectsContainer';
import Settings from './Settings';
import Summary from './Summary';
import UsersProfileTableHOC from './UsersProfileTableHOC';
import VisibilitySettings from './VisibilitySettings';

import styles from './OrganisationProfile.module.scss';

interface IOrganisationProfileProps {
	handleChangeStatusOrganisation?: any;
	handleUpdateOrganisation: any;
	isLoadingProfile: boolean;
	isLoadingUpdateOrganisation: boolean;
	isSettings?: boolean;
	organisationId: string;
	profile: IOrganisationProfile;
}

const OrganisationProfile = ({
	handleChangeStatusOrganisation = () => null,
	handleUpdateOrganisation,
	isLoadingProfile,
	isLoadingUpdateOrganisation,
	isSettings = false,
	organisationId,
	profile,
}: IOrganisationProfileProps) => {
	const navigate = useNavigate();
	const organisationTypes = profile?.types?.map((type: TSelectOption) => type?.name);

	const { accessPermission } = useAppSelector(auth);
	const { isProductionIdams } = useAppSelector(common);

	const {
		platformOwnerAdministrator,
		assetOwnerManageRequests,
		assetOwnerOrganizationAdministrator,
		networkOperatorOrganizationAdministrator,
		portalOwner,
	} = accessPermission;

	const isAOAccessRoleAvailability = profile?.roleAvailability
		?.map(role => role.name).some(role => AOAccessRoleAvailability.includes(role));

	const isAOOrganization = organisationTypes?.includes(EOrganizationType.assetOwner);
	const isAOVisibilitySettings = platformOwnerAdministrator || assetOwnerOrganizationAdministrator;

	const isMNOOrganizationType = profile.types
		?.some((type: { name: string }) => {
			return type.name === EOrganizationType.networkOperator
				|| type.name === EOrganizationType.assetConsumer;
		});
	const isAOOrganizationType = profile.types
		?.some((type: { name: string }) => type.name === EOrganizationType.assetOwner);
		profile.roleAvailability;
	const isAOVisible = (
		platformOwnerAdministrator ||
		assetOwnerManageRequests ||
		assetOwnerOrganizationAdministrator
	) && isAOOrganization;

	const isSettingsTabVisible = platformOwnerAdministrator
		&& organisationTypes?.includes(EOrganizationType.networkOperator);

	const isKaiAssistantTabVisible = platformOwnerAdministrator ||
		((assetOwnerOrganizationAdministrator || networkOperatorOrganizationAdministrator)
			&& !profile.isKaiAssistantHidden);

	const isPortalOwner = platformOwnerAdministrator || portalOwner;

	const tabs = getTabs({
		isSettingsTabVisible,
		isAOSettingsTabVisible: isAOVisible,
		isAssetsTabVisible: isAOVisible,
		isKaiAssistantTabVisible,
		isProjectsTabVisible: (isPortalOwner || profile.isPortalRoleEnabled) && !isProductionIdams,
		isAOAccessTabVisible: platformOwnerAdministrator && isMNOOrganizationType && isAOAccessRoleAvailability,
		isVisibilityTabVisible: isAOVisibilitySettings && isAOOrganizationType,
	});

	const [currentTab, setCurrentTab] = useState(tabsNames.summary);

	const [isOrganisationFormModal, setIsOrganisationFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isConfirmBlockModalOpen, setIsConfirmBlockModalOpen] = useState(false);

	const onBackClickHandler = () => navigate(backPagePath);

	const toggleOrganisationModal = () => setIsOrganisationFormModal(prevState => !prevState);

	const closeOrganisationForm = () => setIsOrganisationFormModal(false);

	const updatedAt = formatDate(+profile?.updatedAt);
	const createdAt = formatDate(+profile?.createdAt);

	const toggleConfirmBlockUnblockModal = () => setIsConfirmBlockModalOpen(prevState => !prevState);
	const changeStatus = () => {
		handleChangeStatusOrganisation({
			id: organisationId,
			isActive: profile?.isActive,
			closeModal: toggleConfirmBlockUnblockModal,
		});
	};

	const getCurrentTab = () => {
		switch (currentTab) {
			case tabsNames.summary:
				return (
					<Summary
						profile={profile}
						toggleOrganisationModal={toggleOrganisationModal}
					/>
				);
			case tabsNames.users:
				return (
					<UsersProfileTableHOC
						adminCount={profile.adminCount}
						id={organisationId}
						organisationName={profile.name}
						organisationTypes={organisationTypes as unknown as TOrganizationType[]}
					/>
				);
			case tabsNames.settings:
				return <Settings organisationId={organisationId} />;
			case tabsNames.assets:
				return <AssetsDashboard id={organisationId} />;
			case tabsNames.aoSettings:
				return (
					<AssetOwnerSettings
						isEditMode={platformOwnerAdministrator}
						organisationId={organisationId}
					/>
				);
			case tabsNames.kaiAssistants:
				return (
					<ManageKaiAssistant
						isPlatformOwner={platformOwnerAdministrator}
						organisationId={organisationId}
						profile={profile}
					/>
				);
			case tabsNames.projects:
				return (
					isPortalOwner
						? <PlatformOwnerProjectsContainer organisationId={organisationId} />
						: <OrganisationAdminProjectsContainer organizationId={organisationId} />
				);
			case tabsNames.aoAccess:
				return <AOAccess organisationId={organisationId} />;
			case tabsNames.visibilitySettings:
				return <VisibilitySettings organisationId={organisationId} />;
			default:
				return (
					<Summary
						profile={profile}
						toggleOrganisationModal={toggleOrganisationModal}
					/>
				);
		}
	};

	const confirmModalQuestion = profile?.isActive
		? 'Are you sure you want to block this Organisation?'
		: 'Are you sure you want to activate this Organisation?';
	const confirmModalMeta = profile?.isActive
		? 'All Users will lose their access to the account. All Assets on Map will be hidden.'
		: 'All Users and Assets will be activated.';

	return (
		<div className={styles.organisationProfileContainer}>
			{isLoadingProfile && <SpinnerOverlay />}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isOrganisationFormModal}
				onClose={closeOrganisationForm}
				title='Edit organisation'
				width={isSettings ? MODAL_WIDTH_580 : '772px'}
			>
				<OrganisationForm
					closeModals={closeOrganisationForm}
					isDirtyFormModal={isDirtyFormModal}
					isEditMode
					isLoadingForm={isLoadingUpdateOrganisation}
					isSettings={isSettings}
					profile={profile}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={handleUpdateOrganisation}
					toggleOrganisationModal={toggleOrganisationModal}
				/>
			</Modal>
			{!isSettings
				? <Button
					icon={Icons.arrowLeft()}
					onClick={onBackClickHandler}
					text='Back'
					withoutBackground
				/>
				: <PageTitle
					icon={Icons.settingsWhite()}
					title={EAdminPanelPageTitles.OrganisationSettings}
				/>
			}
			<div
				className={styles.header}
				style={{
					marginTop: isSettings ? '0' : '10px',
				}}
			>
				<div className={cn(styles.whiteWrapper, styles.nameSection)}>
					<TextAvatar
						avatarId={profile?.avatar?.id}
						fontSize='20px'
						fullName={profile?.name}
						id={profile.id}
						isProfileLogo
						size='75px'
					/>
					<div>
						<div className={styles.shortName}>{profile?.name}</div>
						<div className={styles.location}>
							{Icons.earthIcon()}
							{profile?.country?.name}{!!profile?.city && `, ${profile.city}`}
						</div>
						<div className={styles.position}>
							{!!profile?.types?.length
								&& profile.types.map((type: { title: string }) => type.title).join(', ')
							}
						</div>
					</div>
				</div>
				<div className={cn(styles.whiteWrapper, styles.statusSection)}>
					{!isSettings
						&& <div className={styles.checkbox}>
							<SliderCheckbox
								checked={!!profile?.isActive}
								id='status'
								label='Account Status'
								onChange={toggleConfirmBlockUnblockModal}
							/>
						</div>
					}
					<div className={styles.date}><span>Creation date:</span>&nbsp;{createdAt}</div>
					<div className={styles.date}><span>Update date:</span>&nbsp;{updatedAt}</div>
				</div>
			</div>
			<ProfileTabSection
				contentClassName={styles.profileTabSection}
				currentTab={currentTab}
				getCurrentTab={getCurrentTab}
				setCurrentTab={setCurrentTab}
				tabs={tabs}
			/>

			{isConfirmBlockModalOpen && (
				<Modal
					isLoading={isLoadingUpdateOrganisation}
					isOpen={isConfirmBlockModalOpen}
					onClose={toggleConfirmBlockUnblockModal}
					title='Confirm action'
					width={MODAL_WIDTH_580}
				>
					<Confirm
						onClose={toggleConfirmBlockUnblockModal}
						onSubmit={changeStatus}
						question={confirmModalQuestion}
					>
						{confirmModalMeta}
					</Confirm>
				</Modal>
			)}
		</div>
	);
};

export default OrganisationProfile;
